import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Database, Server, Loader2, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const GoogleIcon = ({ testId }) => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-testid={testId}>
    <path
      fill="#EA4335"
      d="M12 10.2v3.95h5.52c-.22 1.19-.88 2.2-1.88 2.88v2.39h3.05c1.78-1.64 2.8-4.05 2.8-6.94 0-.67-.07-1.31-.2-1.93H12z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.43 0 4.47-.8 5.96-2.16l-3.05-2.39c-.85.58-1.95.92-2.91.92-2.24 0-4.14-1.51-4.82-3.54H4.03v2.46C5.5 21.1 8.5 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M7.18 15.83a5.5 5.5 0 0 1 0-3.66V9.7H4.03a10.9 10.9 0 0 0 0 4.6l3.15-2.47z"
    />
    <path
      fill="#4285F4"
      d="M12 5.1c1.32 0 2.5.45 3.44 1.34l2.58-2.58C16.46 2.35 14.43 1.5 12 1.5 8.5 1.5 5.5 3.4 4.03 6.23l3.15 2.47C7.86 6.61 9.76 5.1 12 5.1z"
    />
  </svg>
);

const LoginPage = () => {
  const { login, loginWithGoogle, loginAsAdmin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [siteName, setSiteName] = useState("AIQueryStudio");

  // Master admin bypass state
  const [showBypass, setShowBypass] = useState(false);
  const [masterKey, setMasterKey] = useState("");
  const [bypassLoading, setBypassLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/settings/public`).then(res => {
      if (res.data.logo_url) setLogoUrl(res.data.logo_url);
      if (res.data.site_name) setSiteName(res.data.site_name);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success("Login successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    loginWithGoogle();
  };

  const handleMasterBypass = async (e) => {
    e.preventDefault();
    if (!masterKey.trim()) return;
    setBypassLoading(true);
    try {
      await loginAsAdmin(masterKey);
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid master key");
    } finally {
      setBypassLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1762279388956-1c098163a2a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwZGF0YSUyMGZsb3d8ZW58MHx8fHwxNzcyNDM0NDE5fDA&ixlib=rb-4.1.0&q=85)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#09090b] via-transparent to-[#09090b]" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity w-fit"
          >
            <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
              {logoUrl ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-8 h-8 object-contain" />
              ) : (
                <Database className="w-8 h-8 text-primary" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-foreground tracking-tight">{siteName}</h1>
          </button>
          <p className="text-xl text-muted-foreground max-w-md leading-relaxed">
            Professional database management for MS SQL Server and MongoDB. Query, explore, and manage your data with ease.
          </p>
          <div className="flex gap-4 mt-12">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Server className="w-4 h-4 text-primary" />
              <span>MS SQL Server</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Database className="w-4 h-4 text-accent" />
              <span>MongoDB</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="space-y-1">
            <button 
              onClick={() => navigate('/')} 
              className="flex items-center gap-2 mb-2 lg:hidden hover:opacity-80 transition-opacity w-fit"
            >
              {logoUrl ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-6 h-6 object-contain" />
              ) : (
                <Database className="w-6 h-6 text-primary" />
              )}
              <span className="font-bold text-lg">{siteName}</span>
            </button>
            <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
            <CardDescription>Sign in to access your databases</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Google SSO Button */}
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2 h-11"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                data-testid="google-login-btn"
              >
                {googleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <GoogleIcon testId="google-login-icon" />
                )}
                Continue with Google
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Google sign-in is available on desktop browsers only
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="bg-background"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>
            <div className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                Create account
              </Link>
            </div>

            {/* ── Master Admin Bypass ─────────────────────────── */}
            <div className="pt-2 border-t border-border/50">
              <button
                type="button"
                onClick={() => { setShowBypass(v => !v); setMasterKey(""); }}
                className="flex items-center gap-1.5 mx-auto text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                data-testid="admin-bypass-toggle"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Admin access
                {showBypass ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>

              {showBypass && (
                <form
                  onSubmit={handleMasterBypass}
                  className="mt-3 p-3 rounded-lg bg-muted/40 border border-border/50 space-y-2"
                  data-testid="admin-bypass-form"
                >
                  <p className="text-xs text-muted-foreground text-center">Enter the master key to access admin panel</p>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      placeholder="Master key"
                      value={masterKey}
                      onChange={e => setMasterKey(e.target.value)}
                      className="bg-background h-9 text-sm flex-1"
                      autoFocus
                      data-testid="admin-bypass-key-input"
                    />
                    <Button
                      type="submit"
                      size="sm"
                      className="h-9 px-4 bg-primary hover:bg-primary/90 shrink-0"
                      disabled={bypassLoading || !masterKey.trim()}
                      data-testid="admin-bypass-submit-btn"
                    >
                      {bypassLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Go"}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
